import is from '@sindresorhus/is';
import {
    $applyNodeReplacement,
    $isRangeSelection,
    type BaseSelection,
    type DOMConversionMap,
    type EditorConfig,
    ElementNode,
    type LexicalNode,
    type LexicalUpdateJSON,
    type NodeKey,
    type RangeSelection,
    type SerializedElementNode,
    type Spread,
} from 'lexical';

export type SerializedCommentMarkNode = Spread<
    {
        commentId: string;
    },
    SerializedElementNode
>;

const COMMENT_MARK_CLASS = 'editor-comment-mark';

export const COMMENT_ID_ATTRIBUTE = 'data-comment-id';

const convertCommentMarkElement = (element: HTMLElement): { node: CommentMarkNode | null } => {
    const commentId = element.getAttribute(COMMENT_ID_ATTRIBUTE);

    return { node: is.null(commentId) ? null : $createCommentMarkNode(commentId) };
};

export class CommentMarkNode extends ElementNode {
    __commentId: string;

    static override getType(): string {
        return 'comment-mark';
    }

    static override clone(node: CommentMarkNode): CommentMarkNode {
        return new CommentMarkNode(node.__commentId, node.__key);
    }

    static override importDOM(): DOMConversionMap | null {
        return {
            mark: (element: HTMLElement) => {
                if (!element.hasAttribute(COMMENT_ID_ATTRIBUTE)) {
                    return null;
                }

                return {
                    conversion: convertCommentMarkElement,
                    priority: 4,
                };
            },
        };
    }

    static override importJSON(serializedNode: SerializedCommentMarkNode): CommentMarkNode {
        return $createCommentMarkNode(serializedNode.commentId).updateFromJSON(serializedNode);
    }

    constructor(commentId: string, key?: NodeKey) {
        super(key);
        this.__commentId = commentId;
    }

    getCommentId(): string {
        return this.getLatest().__commentId;
    }

    setCommentId(commentId: string): this {
        const self = this.getWritable();
        self.__commentId = commentId;
        return self;
    }

    override createDOM(_config: EditorConfig): HTMLElement {
        const element = document.createElement('mark');
        element.classList.add(COMMENT_MARK_CLASS);
        element.setAttribute(COMMENT_ID_ATTRIBUTE, this.__commentId);
        return element;
    }

    override updateDOM(prevNode: CommentMarkNode, element: HTMLElement): boolean {
        if (prevNode.__commentId !== this.__commentId) {
            element.setAttribute(COMMENT_ID_ATTRIBUTE, this.__commentId);
        }

        return false;
    }

    override updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedCommentMarkNode>): this {
        return super.updateFromJSON(serializedNode).setCommentId(serializedNode.commentId);
    }

    override exportJSON(): SerializedCommentMarkNode {
        return {
            ...super.exportJSON(),
            commentId: this.getCommentId(),
        };
    }

    override insertNewAfter(_selection: RangeSelection, restoreSelection = true): null | ElementNode {
        const markNode = $createCommentMarkNode(this.__commentId);
        this.insertAfter(markNode, restoreSelection);
        return markNode;
    }

    override canInsertTextBefore(): true {
        return true;
    }

    override canInsertTextAfter(): false {
        return false;
    }

    override canBeEmpty(): false {
        return false;
    }

    override isInline(): true {
        return true;
    }

    override extractWithChild(child: LexicalNode, selection: BaseSelection, destination: 'clone' | 'html'): boolean {
        if (!$isRangeSelection(selection) || destination === 'html') {
            return false;
        }

        const anchorNode = selection.anchor.getNode();
        const focusNode = selection.focus.getNode();
        const isBackward = selection.isBackward();
        const selectionLength = isBackward
            ? selection.anchor.offset - selection.focus.offset
            : selection.focus.offset - selection.anchor.offset;

        return (
            this.isParentOf(child) &&
            this.isParentOf(anchorNode) &&
            this.isParentOf(focusNode) &&
            this.getTextContent().length === selectionLength
        );
    }

    override excludeFromCopy(destination: 'clone' | 'html'): boolean {
        return destination !== 'clone';
    }
}

export const $createCommentMarkNode = (commentId: string): CommentMarkNode =>
    $applyNodeReplacement(new CommentMarkNode(commentId));

export const $isCommentMarkNode = (node: LexicalNode | null | undefined): node is CommentMarkNode =>
    node instanceof CommentMarkNode;

export const $unwrapCommentMarkNode = (node: CommentMarkNode): void => {
    const children = node.getChildren();
    let target: LexicalNode | null = null;

    for (const child of children) {
        if (is.null(target)) {
            node.insertBefore(child);
        } else {
            target.insertAfter(child);
        }
        target = child;
    }

    node.remove();
};
